// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Return + Debit Note helpers (Stage 6P.8).
//
// Pure ESM module — exercised by the frontend node:test runner.
//
// Frontend mirror of backend Purchase Return math + payload shaping.
// The backend
// (`backend/src/services/erp/purchaseReturn/*`
//  — plus the shared accounting `voucherPosting.service.js`)
// remains the source of truth for:
//   - return_no allocation (PRET-YYYYMMDD-NNNNNN)
//   - debit_note_no allocation (PDN-YYYYMMDD-NNNNNN)
//   - duplicate-block decisions (one voucher per return_id)
//   - inventory_stock_ledger idempotency on the tuple
//     (source_type='PURCHASE_RETURN', source_id, source_line_id,
//      movement_type)
//   - line balance computation (return_balance_qty etc.) from
//     already_returned_qty against the underlying inwarded GRN /
//     purchase bill line
//   - voucher-side debit/credit balance enforcement (DEBIT_NOTE)
//   - ITC reversal posting against purchase_gst_itc_register
//
// These helpers exist only so the Stage 6P.8 form / preview / register
// UI can show consistent totals, status badges and capability gates
// while the operator drafts the return. The API recomputes on POST and
// is the final authority.
//
// Mirrors the style of:
//   - frontend/src/pages/erp/supplierPayments/supplierPaymentHelpers.js
//   - backend/src/services/erp/purchaseReturn/purchaseReturn.constants.js
//     (enum lists — frontend mirrors are sealed with Object.freeze so
//     callers can't mutate the shared list by accident).
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// Enum mirrors — kept aligned with backend
// services/erp/purchaseReturn/purchaseReturn.constants.js.
// Frozen so callers can't mutate the shared lists by accident.
// ─────────────────────────────────────────────────────────────────

export const RETURN_TYPES = Object.freeze([
  'MATERIAL_RETURN',
  'RATE_ADJUSTMENT',
  'TAX_ADJUSTMENT',
  'QUANTITY_ADJUSTMENT'
]);

export const REASON_CODES = Object.freeze([
  'DAMAGED',
  'WRONG_ITEM',
  'QUALITY_REJECTED',
  'SHORT_SUPPLY_ADJUSTMENT',
  'RATE_DIFFERENCE',
  'EXCESS_BILLING',
  'SUPPLIER_REPLACEMENT',
  'OTHER'
]);

export const RETURN_STATUS = Object.freeze([
  'DRAFT',
  'CONFIRMED',
  'POSTED',
  'POSTING_FAILED',
  'CANCELLED'
]);

export const POSTING_STATUS = Object.freeze([
  'NOT_POSTED',
  'POSTED',
  'POSTING_FAILED',
  'CANCELLED'
]);

export const EVENT_TYPES = Object.freeze([
  'CREATED',
  'UPDATED',
  'CONFIRMED',
  'POSTED',
  'POSTING_FAILED',
  'CANCELLED',
  'VOUCHER_CANCEL_FAILED',
  'DUPLICATE_BLOCKED'
]);

export const ITEM_TYPES = Object.freeze([
  'GLASS',
  'HARDWARE',
  'ACCESSORY',
  'PROFILE',
  'UPVC',
  'ALUMINIUM',
  'SERVICE',
  'JOBWORK',
  'OTHER'
]);

export const REVERSAL_STATUS = Object.freeze([
  'REVERSED',
  'CANCELLED'
]);

export const VOUCHER_TYPE = 'DEBIT_NOTE';


// ─────────────────────────────────────────────────────────────────
// Private constants used by the math + classifier helpers.
// ─────────────────────────────────────────────────────────────────

const RETURN_TYPES_SET   = new Set(RETURN_TYPES);
const REASON_CODES_SET   = new Set(REASON_CODES);
const RETURN_STATUS_SET  = new Set(RETURN_STATUS);

// Half-paisa tolerance — matches backend BALANCE_EPSILON.
const BALANCE_EPSILON = 0.01;

// String constants — kept private to dodge accidental re-export drift.
const STATUS_DRAFT          = 'DRAFT';
const STATUS_CONFIRMED      = 'CONFIRMED';
const STATUS_POSTED         = 'POSTED';
const STATUS_POSTING_FAILED = 'POSTING_FAILED';
const STATUS_CANCELLED      = 'CANCELLED';


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric coercion — mirrors backend toNumber.
// Null / undefined / empty string / non-finite all collapse to 0.
// Never throws, never returns NaN.
// ─────────────────────────────────────────────────────────────────

function toNumber(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Half-up rounding — defeats IEEE-754 surprises like 1.005 → 1.0049…
function roundHalfUp(value, decimals) {
  const n = toNumber(value);
  if (n === 0) return 0;
  const factor = Math.pow(10, decimals);
  const sign   = n < 0 ? -1 : 1;
  const abs    = Math.abs(n);
  const scaled = abs * factor + 1e-9;
  return (sign * Math.round(scaled)) / factor;
}

function round2(n) { return roundHalfUp(n, 2); }
function round4(n) { return roundHalfUp(n, 4); }


// ─────────────────────────────────────────────────────────────────
// SQMT / SQFT conversion + length-driven item types — shared by
// the calculateReturn* helpers below and by the line-level recompute
// driver `calculatePurchaseReturnLine`. Mirrors the backend
// `SQMT_TO_SQFT` constant and `LENGTH_ITEM_TYPES` set bit-for-bit so
// the operator's in-form recompute lines up with the server.
// ─────────────────────────────────────────────────────────────────

// 1 SQMT = 10.764 SQFT.
const SQMT_TO_SQFT = 10.764;

const ITEM_TYPE_GLASS = 'GLASS';

// PROFILE / UPVC / ALUMINIUM are length-driven — total_length =
// length_value × qty. Everything else (HARDWARE / ACCESSORY /
// SERVICE / JOBWORK / OTHER) is qty-only.
const LENGTH_ITEM_TYPES = new Set([
  'PROFILE',
  'UPVC',
  'ALUMINIUM'
]);


// ─────────────────────────────────────────────────────────────────
// Snake / camel access — preview rows arrive from the API in camel,
// but ledger / posting rows arrive in both depending on whether they
// were freshly mapped or replayed from a server cache.
// We probe both and never mutate the caller.
// ─────────────────────────────────────────────────────────────────

function pick(obj, snake, camel, fallback) {
  if (!obj || typeof obj !== 'object') return fallback;
  if (snake in obj && obj[snake] !== undefined) return obj[snake];
  if (camel in obj && obj[camel] !== undefined) return obj[camel];
  return fallback;
}

function pickStringUpper(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim().toUpperCase();
}

function pickString(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim();
}


// ─────────────────────────────────────────────────────────────────
// Row normalisers — the API returns camelCase rows already, but the
// grid + drawer also consume rows replayed from local state or
// fixture data that may carry either casing. These normalisers return
// a canonical camelCase shape with widened numbers, never mutating
// the input.
//
// Anything we don't recognise is preserved on the way out so the
// grid can show columns we don't know about (e.g. future tenant
// extensions) without losing data.
// ─────────────────────────────────────────────────────────────────

export function normalizePurchaseReturn(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.id                            = pick(row, 'id', 'id', null);
  out.returnNo                      = pick(row, 'return_no',   'returnNo',   null);
  out.returnDate                    = pick(row, 'return_date', 'returnDate', null);

  out.returnType                    = pickStringUpper(row, 'return_type',  'returnType')  || null;
  out.reasonCode                    = pickStringUpper(row, 'reason_code',  'reasonCode')  || null;

  out.supplierId                    = pick(row, 'supplier_id',    'supplierId',    null);
  out.supplierName                  = pick(row, 'supplier_name',  'supplierName',  null);
  out.supplierGstin                 = pick(row, 'supplier_gstin', 'supplierGstin', null);

  out.purchaseOrderId               = pick(row, 'purchase_order_id', 'purchaseOrderId', null);
  out.poNo                          = pick(row, 'po_no',             'poNo',            null);
  out.goodsReceiptId                = pick(row, 'goods_receipt_id',  'goodsReceiptId',  null);
  out.grnNo                         = pick(row, 'grn_no',            'grnNo',           null);
  out.purchaseBillId                = pick(row, 'purchase_bill_id',  'purchaseBillId',  null);
  out.purchaseBillNo                = pick(row, 'purchase_bill_no',  'purchaseBillNo',  null);
  out.purchaseAccountingPostingId   = pick(row, 'purchase_accounting_posting_id', 'purchaseAccountingPostingId', null);
  out.purchaseAccountingPostingNo   = pick(row, 'purchase_accounting_posting_no', 'purchaseAccountingPostingNo', null);

  out.debitNoteNo                   = pick(row, 'debit_note_no',           'debitNoteNo',           null);
  out.debitNoteDate                 = pick(row, 'debit_note_date',         'debitNoteDate',         null);
  out.supplierCreditNoteNo          = pick(row, 'supplier_credit_note_no', 'supplierCreditNoteNo',  null);
  out.supplierCreditNoteDate        = pick(row, 'supplier_credit_note_date','supplierCreditNoteDate', null);

  out.taxableAmount                 = toNumber(pick(row, 'taxable_amount', 'taxableAmount', 0));
  out.cgstAmount                    = toNumber(pick(row, 'cgst_amount',    'cgstAmount',    0));
  out.sgstAmount                    = toNumber(pick(row, 'sgst_amount',    'sgstAmount',    0));
  out.igstAmount                    = toNumber(pick(row, 'igst_amount',    'igstAmount',    0));
  out.totalTax                      = toNumber(pick(row, 'total_tax',      'totalTax',      0));
  out.roundOff                      = toNumber(pick(row, 'round_off',      'roundOff',      0));
  out.grandTotal                    = toNumber(pick(row, 'grand_total',    'grandTotal',    0));

  out.inventoryPosted               = !!pick(row, 'inventory_posted',    'inventoryPosted',    false);
  out.inventoryPostedAt             = pick(row, 'inventory_posted_at', 'inventoryPostedAt', null);
  out.accountingVoucherId           = pick(row, 'accounting_voucher_id', 'accountingVoucherId', null);
  out.accountingVoucherNo           = pick(row, 'accounting_voucher_no', 'accountingVoucherNo', null);
  out.voucherType                   = pick(row, 'voucher_type',          'voucherType',         null);
  out.voucherDate                   = pick(row, 'voucher_date',          'voucherDate',         null);

  out.status                        = pickStringUpper(row, 'status',         'status')        || null;
  out.postingStatus                 = pickStringUpper(row, 'posting_status', 'postingStatus') || null;
  out.failureReason                 = pick(row, 'failure_reason',      'failureReason',      null);
  out.cancellationReason            = pick(row, 'cancellation_reason', 'cancellationReason', null);

  out.postedAt                      = pick(row, 'posted_at',    'postedAt',    null);
  out.postedBy                      = pick(row, 'posted_by',    'postedBy',    null);
  out.cancelledAt                   = pick(row, 'cancelled_at', 'cancelledAt', null);
  out.cancelledBy                   = pick(row, 'cancelled_by', 'cancelledBy', null);

  out.remarks                       = pick(row, 'remarks',    'remarks',   null);
  out.createdBy                     = pick(row, 'created_by', 'createdBy', null);
  out.updatedBy                     = pick(row, 'updated_by', 'updatedBy', null);
  out.createdAt                     = pick(row, 'created_at', 'createdAt', null);
  out.updatedAt                     = pick(row, 'updated_at', 'updatedAt', null);

  return out;
}

export function normalizePurchaseReturnLine(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.id                       = pick(row, 'id', 'id', null);
  out.purchaseReturnId         = pick(row, 'purchase_return_id', 'purchaseReturnId', null);
  out.lineNo                   = toNumber(pick(row, 'line_no', 'lineNo', 0)) || 0;

  out.inventoryStockLedgerId   = pick(row, 'inventory_stock_ledger_id', 'inventoryStockLedgerId', null);
  out.goodsReceiptLineId       = pick(row, 'goods_receipt_line_id',    'goodsReceiptLineId',    null);
  out.purchaseBillLineId       = pick(row, 'purchase_bill_line_id',    'purchaseBillLineId',    null);
  out.purchaseOrderLineId      = pick(row, 'purchase_order_line_id',   'purchaseOrderLineId',   null);

  out.itemId                   = pick(row, 'item_id',   'itemId',   null);
  out.itemType                 = pickStringUpper(row, 'item_type', 'itemType') || null;
  out.itemName                 = pick(row, 'item_name', 'itemName', null);
  out.itemDesc                 = pick(row, 'item_desc', 'itemDesc', null);
  out.stockName                = pick(row, 'stock_name', 'stockName', null);
  out.itemDescription          = pick(row, 'item_description', 'itemDescription', null);

  out.thicknessMm              = toNumber(pick(row, 'thickness_mm', 'thicknessMm', 0));
  out.widthMm                  = toNumber(pick(row, 'width_mm',  'widthMm',  0));
  out.heightMm                 = toNumber(pick(row, 'height_mm', 'heightMm', 0));

  out.returnQty                = toNumber(pick(row, 'return_qty',  'returnQty',  0));
  out.returnSqmt               = toNumber(pick(row, 'return_sqmt', 'returnSqmt', 0));
  out.returnSqft               = toNumber(pick(row, 'return_sqft', 'returnSqft', 0));
  out.lengthValue              = toNumber(pick(row, 'length_value', 'lengthValue', 0));
  out.lengthUom                = pick(row, 'length_uom', 'lengthUom', null);
  out.returnTotalLength        = toNumber(pick(row, 'return_total_length', 'returnTotalLength', 0));

  out.uom                      = pick(row, 'uom', 'uom', null);
  out.rate                     = toNumber(pick(row, 'rate',          'rate',          0));
  out.grossAmount              = toNumber(pick(row, 'gross_amount',  'grossAmount',   0));
  out.discountPercent          = toNumber(pick(row, 'discount_percent', 'discountPercent', 0));
  out.discountAmount           = toNumber(pick(row, 'discount_amount',  'discountAmount',  0));
  out.taxableAmount            = toNumber(pick(row, 'taxable_amount', 'taxableAmount', 0));
  out.gstRate                  = toNumber(pick(row, 'gst_rate',       'gstRate',       0));
  out.cgstAmount               = toNumber(pick(row, 'cgst_amount',    'cgstAmount',    0));
  out.sgstAmount               = toNumber(pick(row, 'sgst_amount',    'sgstAmount',    0));
  out.igstAmount               = toNumber(pick(row, 'igst_amount',    'igstAmount',    0));
  out.lineTotal                = toNumber(pick(row, 'line_total',     'lineTotal',     0));

  out.alreadyReturnedQty       = toNumber(pick(row, 'already_returned_qty', 'alreadyReturnedQty', 0));
  out.returnBalanceQty         = toNumber(pick(row, 'return_balance_qty',   'returnBalanceQty',   0));
  out.alreadyReturnedSqmt      = toNumber(pick(row, 'already_returned_sqmt','alreadyReturnedSqmt',0));
  out.returnBalanceSqmt        = toNumber(pick(row, 'return_balance_sqmt',  'returnBalanceSqmt',  0));
  out.alreadyReturnedSqft      = toNumber(pick(row, 'already_returned_sqft','alreadyReturnedSqft',0));
  out.returnBalanceSqft        = toNumber(pick(row, 'return_balance_sqft',  'returnBalanceSqft',  0));
  out.alreadyReturnedTotalLength = toNumber(pick(row, 'already_returned_total_length','alreadyReturnedTotalLength',0));
  out.returnBalanceTotalLength   = toNumber(pick(row, 'return_balance_total_length',  'returnBalanceTotalLength',  0));

  out.remarks                  = pick(row, 'remarks',    'remarks',   null);
  out.createdAt                = pick(row, 'created_at', 'createdAt', null);
  out.updatedAt                = pick(row, 'updated_at', 'updatedAt', null);

  return out;
}

export function normalizePurchaseReturnItcReversal(row) {
  if (!row || typeof row !== 'object') return null;
  const out = Object.assign({}, row);

  out.id                          = pick(row, 'id', 'id', null);
  out.purchaseReturnId            = pick(row, 'purchase_return_id',      'purchaseReturnId',      null);
  out.purchaseReturnLineId        = pick(row, 'purchase_return_line_id', 'purchaseReturnLineId',  null);
  out.purchaseGstItcRegisterId    = pick(row, 'purchase_gst_itc_register_id', 'purchaseGstItcRegisterId', null);
  out.purchaseBillId              = pick(row, 'purchase_bill_id',        'purchaseBillId',        null);
  out.purchaseBillLineId          = pick(row, 'purchase_bill_line_id',   'purchaseBillLineId',    null);

  out.debitNoteNo                 = pick(row, 'debit_note_no',           'debitNoteNo',           null);
  out.debitNoteDate               = pick(row, 'debit_note_date',         'debitNoteDate',         null);
  out.supplierInvoiceNo           = pick(row, 'supplier_invoice_no',     'supplierInvoiceNo',     null);
  out.supplierInvoiceDate         = pick(row, 'supplier_invoice_date',   'supplierInvoiceDate',   null);
  out.supplierGstin               = pick(row, 'supplier_gstin',          'supplierGstin',         null);

  out.gstRate                     = toNumber(pick(row, 'gst_rate',           'gstRate',           0));
  out.taxableReversal             = toNumber(pick(row, 'taxable_reversal',   'taxableReversal',   0));
  out.cgstReversal                = toNumber(pick(row, 'cgst_reversal',      'cgstReversal',      0));
  out.sgstReversal                = toNumber(pick(row, 'sgst_reversal',      'sgstReversal',      0));
  out.igstReversal                = toNumber(pick(row, 'igst_reversal',      'igstReversal',      0));
  out.totalItcReversal            = toNumber(pick(row, 'total_itc_reversal', 'totalItcReversal',  0));

  out.reversalStatus              = pickStringUpper(row, 'reversal_status', 'reversalStatus') || null;
  out.remarks                     = pick(row, 'remarks',    'remarks',   null);
  out.createdAt                   = pick(row, 'created_at', 'createdAt', null);
  out.updatedAt                   = pick(row, 'updated_at', 'updatedAt', null);

  return out;
}


// ─────────────────────────────────────────────────────────────────
// Line measurement helpers — exported so the grid editor can drive
// instant recalculation as the operator types qty / width / height
// / length_value. Mirrors the backend area-math bit-for-bit.
// ─────────────────────────────────────────────────────────────────

// Return glass area: widthMm × heightMm × qty ÷ 1,000,000 (mm² → m²).
export function calculateReturnSqmt(widthMm, heightMm, qty) {
  const w = toNumber(widthMm);
  const h = toNumber(heightMm);
  const q = toNumber(qty);
  if (w <= 0 || h <= 0 || q <= 0) return 0;
  return round4((w * h * q) / 1000000);
}

export function calculateReturnSqft(sqmt) {
  const s = toNumber(sqmt);
  if (s <= 0) return 0;
  return round4(s * SQMT_TO_SQFT);
}

// length_value × qty for length-driven items (RFT / RMT / MTR).
// UOM choice is validated upstream — this helper just multiplies.
export function calculateReturnTotalLength(lengthValue, qty) {
  const l = toNumber(lengthValue);
  const q = toNumber(qty);
  if (l <= 0 || q <= 0) return 0;
  return round4(l * q);
}


// ─────────────────────────────────────────────────────────────────
// calculatePurchaseReturnLine — recomputes a single line's
// area / length / discount / tax / total surface from its inputs.
//
// ctx may carry:
//   isInterState: boolean   — drives CGST+SGST vs IGST split.
//
// Returns a NEW object carrying both casings + recomputed totals.
// Anything we don't recognise on the input is preserved on the way
// out. Never throws.
// ─────────────────────────────────────────────────────────────────

export function calculatePurchaseReturnLine(line, ctx) {
  const safe = line && typeof line === 'object' ? line : {};
  const context = ctx && typeof ctx === 'object' ? ctx : {};
  const isInterState = context.isInterState === true;

  const itemType  = pickStringUpper(safe, 'item_type', 'itemType');
  const returnQty = toNumber(pick(safe, 'return_qty', 'returnQty', 0));
  let   widthMm   = toNumber(pick(safe, 'width_mm',     'widthMm',     0));
  let   heightMm  = toNumber(pick(safe, 'height_mm',    'heightMm',    0));
  let   lengthVal = toNumber(pick(safe, 'length_value', 'lengthValue', 0));

  let returnSqmt        = 0;
  let returnSqft        = 0;
  let returnTotalLength = 0;

  if (itemType === ITEM_TYPE_GLASS) {
    returnSqmt        = calculateReturnSqmt(widthMm, heightMm, returnQty);
    returnSqft        = calculateReturnSqft(returnSqmt);
    returnTotalLength = 0;
    lengthVal         = 0;
  } else if (LENGTH_ITEM_TYPES.has(itemType)) {
    returnTotalLength = calculateReturnTotalLength(lengthVal, returnQty);
    returnSqmt        = 0;
    returnSqft        = 0;
    widthMm           = 0;
    heightMm          = 0;
  } else {
    returnSqmt        = 0;
    returnSqft        = 0;
    returnTotalLength = 0;
    widthMm           = 0;
    heightMm          = 0;
    lengthVal         = 0;
  }

  const rate            = toNumber(pick(safe, 'rate',             'rate',             0));
  const discountPercent = toNumber(pick(safe, 'discount_percent', 'discountPercent',  0));
  const gstRate         = toNumber(pick(safe, 'gst_rate',         'gstRate',          0));

  const grossAmount    = round2(returnQty * rate);
  const discountAmount = round2(grossAmount * (discountPercent / 100));
  const taxableAmount  = round2(grossAmount - discountAmount);

  let cgstAmount = 0;
  let sgstAmount = 0;
  let igstAmount = 0;

  if (gstRate > 0 && taxableAmount > 0) {
    if (isInterState) {
      igstAmount = round2(taxableAmount * (gstRate / 100));
    } else {
      const half = round2(taxableAmount * (gstRate / 200));
      cgstAmount = half;
      sgstAmount = half;
    }
  }

  const lineTotal = round2(taxableAmount + cgstAmount + sgstAmount + igstAmount);

  return Object.assign({}, safe, {
    item_type:           itemType || null,
    itemType:            itemType || null,

    return_qty:          returnQty,
    returnQty,
    width_mm:            widthMm,
    widthMm,
    height_mm:           heightMm,
    heightMm,
    length_value:        lengthVal,
    lengthValue:         lengthVal,

    return_sqmt:         returnSqmt,
    returnSqmt,
    return_sqft:         returnSqft,
    returnSqft,
    return_total_length: returnTotalLength,
    returnTotalLength,

    rate,
    gross_amount:        grossAmount,
    grossAmount,
    discount_percent:    discountPercent,
    discountPercent,
    discount_amount:     discountAmount,
    discountAmount,
    taxable_amount:      taxableAmount,
    taxableAmount,
    gst_rate:            gstRate,
    gstRate,
    cgst_amount:         cgstAmount,
    cgstAmount,
    sgst_amount:         sgstAmount,
    sgstAmount,
    igst_amount:         igstAmount,
    igstAmount,
    line_total:          lineTotal,
    lineTotal
  });
}


// ─────────────────────────────────────────────────────────────────
// calculatePurchaseReturnTotals — sums per-line taxable / GST /
// total surface, applies optional rounding ledger and returns the
// header-level totals the form footer renders.
//
// options may carry:
//   roundOff: number — explicit operator override; otherwise the
//                      grand_total is rounded to the nearest rupee
//                      and the delta is surfaced as the rounding
//                      ledger.
//
// Mirrors calculateReturnHeaderTotals in spirit (which still exists
// for legacy callers) but follows the brief's contract surface —
// `lines, options` — so it slots cleanly into the brief-named hook
// chain on the form footer.
// ─────────────────────────────────────────────────────────────────

export function calculatePurchaseReturnTotals(lines, options) {
  let taxable = 0;
  let cgst    = 0;
  let sgst    = 0;
  let igst    = 0;

  if (Array.isArray(lines)) {
    for (const ln of lines) {
      if (!ln || typeof ln !== 'object') continue;
      taxable += toNumber(pick(ln, 'taxable_amount', 'taxableAmount', 0));
      cgst    += toNumber(pick(ln, 'cgst_amount',    'cgstAmount',    0));
      sgst    += toNumber(pick(ln, 'sgst_amount',    'sgstAmount',    0));
      igst    += toNumber(pick(ln, 'igst_amount',    'igstAmount',    0));
    }
  }

  const taxableAmount = round2(taxable);
  const cgstAmount    = round2(cgst);
  const sgstAmount    = round2(sgst);
  const igstAmount    = round2(igst);
  const totalTax      = round2(cgstAmount + sgstAmount + igstAmount);

  const opts = options && typeof options === 'object' ? options : {};

  let roundOff;
  let grandTotal;

  if (opts.roundOff !== undefined && opts.roundOff !== null && opts.roundOff !== '') {
    roundOff   = round2(toNumber(opts.roundOff));
    grandTotal = round2(taxableAmount + totalTax + roundOff);
  } else {
    const preRound = round2(taxableAmount + totalTax);
    grandTotal = Math.round(preRound);
    roundOff   = round2(grandTotal - preRound);
  }

  return {
    taxableAmount,
    cgstAmount,
    sgstAmount,
    igstAmount,
    totalTax,
    roundOff,
    grandTotal
  };
}


// ─────────────────────────────────────────────────────────────────
// Purchase Return payload builder — produces the body for
// POST /api/erp/purchase-returns and PUT /api/erp/purchase-returns/:id.
//
// Maps the camelCase form values rendered by the AntD form to the
// snake_case shape the backend Joi validator expects. Nullable / blank
// keys are stripped so an "empty" instrument field doesn't echo back
// as null in the audit logs. Lines are normalised to the line shape
// the validator accepts.
// ─────────────────────────────────────────────────────────────────

export function buildPurchaseReturnPayload(formValues) {
  const v = formValues && typeof formValues === 'object' ? formValues : {};
  const out = {};

  // Return date.
  const returnDate = pick(v, 'return_date', 'returnDate', null);
  if (returnDate !== null && returnDate !== undefined) {
    if (returnDate instanceof Date && !Number.isNaN(returnDate.getTime())) {
      out.return_date = returnDate.toISOString().slice(0, 10);
    } else {
      const s = String(returnDate).trim();
      if (s) out.return_date = s;
    }
  }

  // Taxonomy.
  const returnType = pickStringUpper(v, 'return_type', 'returnType');
  if (returnType) out.return_type = returnType;

  const reasonCode = pickStringUpper(v, 'reason_code', 'reasonCode');
  if (reasonCode) out.reason_code = reasonCode;

  // Supplier identity.
  const supplierId = pick(v, 'supplier_id', 'supplierId', null);
  if (supplierId !== null && supplierId !== undefined && String(supplierId).trim() !== '') {
    out.supplier_id = String(supplierId).trim();
  }

  const supplierName = pickString(v, 'supplier_name', 'supplierName');
  if (supplierName) out.supplier_name = supplierName;

  const supplierGstin = pickStringUpper(v, 'supplier_gstin', 'supplierGstin');
  if (supplierGstin) out.supplier_gstin = supplierGstin;

  // Source-document linkage.
  const poId = pick(v, 'purchase_order_id', 'purchaseOrderId', null);
  if (poId) out.purchase_order_id = String(poId).trim();
  const poNo = pickString(v, 'po_no', 'poNo');
  if (poNo) out.po_no = poNo;

  const grnId = pick(v, 'goods_receipt_id', 'goodsReceiptId', null);
  if (grnId) out.goods_receipt_id = String(grnId).trim();
  const grnNo = pickString(v, 'grn_no', 'grnNo');
  if (grnNo) out.grn_no = grnNo;

  const billId = pick(v, 'purchase_bill_id', 'purchaseBillId', null);
  if (billId) out.purchase_bill_id = String(billId).trim();
  const billNo = pickString(v, 'purchase_bill_no', 'purchaseBillNo');
  if (billNo) out.purchase_bill_no = billNo;

  const postingId = pick(v, 'purchase_accounting_posting_id', 'purchaseAccountingPostingId', null);
  if (postingId) out.purchase_accounting_posting_id = String(postingId).trim();
  const postingNo = pickString(v, 'purchase_accounting_posting_no', 'purchaseAccountingPostingNo');
  if (postingNo) out.purchase_accounting_posting_no = postingNo;

  // Supplier credit note metadata.
  const supplierCreditNoteNo = pickString(v, 'supplier_credit_note_no', 'supplierCreditNoteNo');
  if (supplierCreditNoteNo) out.supplier_credit_note_no = supplierCreditNoteNo;

  const supplierCreditNoteDate = pick(v, 'supplier_credit_note_date', 'supplierCreditNoteDate', null);
  if (supplierCreditNoteDate !== null && supplierCreditNoteDate !== undefined) {
    if (supplierCreditNoteDate instanceof Date && !Number.isNaN(supplierCreditNoteDate.getTime())) {
      out.supplier_credit_note_date = supplierCreditNoteDate.toISOString().slice(0, 10);
    } else {
      const s = String(supplierCreditNoteDate).trim();
      if (s) out.supplier_credit_note_date = s;
    }
  }

  // Header money.
  const roundOff = pick(v, 'round_off', 'roundOff', undefined);
  if (roundOff !== undefined && roundOff !== null && roundOff !== '') {
    out.round_off = round2(toNumber(roundOff));
  }

  const remarks = pick(v, 'remarks', 'remarks', null);
  if (remarks !== null && remarks !== undefined) {
    const s = String(remarks).trim();
    if (s) out.remarks = s;
  }

  // Lines — required by the validator for MATERIAL_RETURN; left as an
  // empty array otherwise so the validator default kicks in.
  const linesRaw = pick(v, 'lines', 'lines', []);
  if (Array.isArray(linesRaw) && linesRaw.length > 0) {
    out.lines = linesRaw
      .filter((l) => l && typeof l === 'object')
      .map((l, idx) => {
        const line = {};
        line.line_no = toNumber(pick(l, 'line_no', 'lineNo', idx + 1)) || (idx + 1);

        const ledgerId = pick(l, 'inventory_stock_ledger_id', 'inventoryStockLedgerId', null);
        if (ledgerId) line.inventory_stock_ledger_id = String(ledgerId).trim();
        const grnLineId = pick(l, 'goods_receipt_line_id', 'goodsReceiptLineId', null);
        if (grnLineId) line.goods_receipt_line_id = String(grnLineId).trim();
        const billLineId = pick(l, 'purchase_bill_line_id', 'purchaseBillLineId', null);
        if (billLineId) line.purchase_bill_line_id = String(billLineId).trim();
        const poLineId = pick(l, 'purchase_order_line_id', 'purchaseOrderLineId', null);
        if (poLineId) line.purchase_order_line_id = String(poLineId).trim();

        const itemId = pick(l, 'item_id', 'itemId', null);
        if (itemId) line.item_id = String(itemId).trim();
        const itemType = pickStringUpper(l, 'item_type', 'itemType');
        if (itemType) line.item_type = itemType;
        const itemName = pickString(l, 'item_name', 'itemName');
        if (itemName) line.item_name = itemName;
        const itemDesc = pickString(l, 'item_desc', 'itemDesc');
        if (itemDesc) line.item_desc = itemDesc;
        const stockName = pickString(l, 'stock_name', 'stockName');
        if (stockName) line.stock_name = stockName;
        const itemDescription = pickString(l, 'item_description', 'itemDescription');
        if (itemDescription) line.item_description = itemDescription;

        const thickness = pick(l, 'thickness_mm', 'thicknessMm', undefined);
        if (thickness !== undefined && thickness !== null && thickness !== '') {
          line.thickness_mm = toNumber(thickness);
        }
        const width = pick(l, 'width_mm', 'widthMm', undefined);
        if (width !== undefined && width !== null && width !== '') {
          line.width_mm = toNumber(width);
        }
        const height = pick(l, 'height_mm', 'heightMm', undefined);
        if (height !== undefined && height !== null && height !== '') {
          line.height_mm = toNumber(height);
        }

        const returnQty = pick(l, 'return_qty', 'returnQty', undefined);
        if (returnQty !== undefined && returnQty !== null && returnQty !== '') {
          line.return_qty = toNumber(returnQty);
        }

        const uom = pickString(l, 'uom', 'uom');
        if (uom) line.uom = uom;

        const rate = pick(l, 'rate', 'rate', undefined);
        if (rate !== undefined && rate !== null && rate !== '') {
          line.rate = round2(toNumber(rate));
        }

        const discPct = pick(l, 'discount_percent', 'discountPercent', undefined);
        if (discPct !== undefined && discPct !== null && discPct !== '') {
          line.discount_percent = toNumber(discPct);
        }

        const gstRate = pick(l, 'gst_rate', 'gstRate', undefined);
        if (gstRate !== undefined && gstRate !== null && gstRate !== '') {
          line.gst_rate = toNumber(gstRate);
        }

        const lRemarks = pickString(l, 'remarks', 'remarks');
        if (lRemarks) line.remarks = lRemarks;

        return line;
      });
  } else {
    out.lines = [];
  }

  return out;
}


// ─────────────────────────────────────────────────────────────────
// Per-line return-qty validator. balance is the line's current return
// balance (post any prior active returns). The header-level validator
// is the right place to check sum invariants; this helper only polices
// a single line row.
//
// Returns { ok, errors:[{ path, message }] }.
// ─────────────────────────────────────────────────────────────────

export function validateReturnLine(line, balance) {
  const errors = [];

  if (!line || typeof line !== 'object') {
    return { ok: false, errors: [{ path: '', message: 'Line is required.' }] };
  }

  const itemName = pickString(line, 'item_name', 'itemName');
  if (!itemName) {
    errors.push({ path: 'item_name', message: 'Item name is required.' });
  }

  const uom = pickString(line, 'uom', 'uom');
  if (!uom) {
    errors.push({ path: 'uom', message: 'UOM is required.' });
  }

  const qtyRaw = pick(line, 'return_qty', 'returnQty', undefined);
  const qty    = toNumber(qtyRaw);
  if (qtyRaw === undefined || qtyRaw === null || qtyRaw === '') {
    errors.push({ path: 'return_qty', message: 'Return qty is required.' });
  } else if (!(qty > 0)) {
    errors.push({ path: 'return_qty', message: 'Return qty must be greater than zero.' });
  }

  // Balance check — only when balance is a finite number.
  if (typeof balance === 'number' && Number.isFinite(balance)) {
    if (qty - balance > BALANCE_EPSILON) {
      errors.push({
        path: 'return_qty',
        message:
          `Return qty (${round2(qty)}) exceeds available balance (${round2(balance)}).`
      });
    }
  }

  const discPct = toNumber(pick(line, 'discount_percent', 'discountPercent', 0));
  if (discPct < 0 || discPct > 100) {
    errors.push({
      path: 'discount_percent',
      message: 'Discount percent must be between 0 and 100.'
    });
  }

  const gstRate = toNumber(pick(line, 'gst_rate', 'gstRate', 0));
  if (gstRate < 0 || gstRate > 28) {
    errors.push({
      path: 'gst_rate',
      message: 'GST rate must be between 0 and 28.'
    });
  }

  return { ok: errors.length === 0, errors };
}


// ─────────────────────────────────────────────────────────────────
// Brief-named per-line validator — `validatePurchaseReturnLine`.
//
// Wraps `validateReturnLine` and adds:
//   - item-type-aware measurement requirements: GLASS lines must
//     have width_mm and height_mm; PROFILE / UPVC / ALUMINIUM lines
//     must have a positive length_value.
//   - rate >= 0 sanity.
//   - reads the returnable balance directly off the line via
//     `return_balance_qty` / `returnBalanceQty` so callers don't
//     need to thread the balance through a second argument.
//
// Returns { ok, errors:[{ path, message }] } — same shape as
// validateReturnLine.
// ─────────────────────────────────────────────────────────────────

export function validatePurchaseReturnLine(line) {
  if (!line || typeof line !== 'object') {
    return { ok: false, errors: [{ path: '', message: 'Line is required.' }] };
  }

  const balanceRaw = pick(line, 'return_balance_qty', 'returnBalanceQty', undefined);
  const balance = balanceRaw === undefined || balanceRaw === null || balanceRaw === ''
    ? undefined
    : toNumber(balanceRaw);

  const base = validateReturnLine(line, balance);
  const errors = Array.isArray(base.errors) ? base.errors.slice() : [];

  // Accept either item_id OR item_name as the "Item is required"
  // gate — matches the backend validator's lenient identity check.
  const itemId   = pick(line, 'item_id',   'itemId',   null);
  const itemName = pickString(line, 'item_name', 'itemName');
  const hasItemId = itemId !== null && itemId !== undefined &&
                    String(itemId).trim() !== '';
  if (hasItemId && !itemName) {
    // validateReturnLine flagged item_name — but item_id satisfies
    // identity; drop that error so the form doesn't double-warn.
    for (let i = errors.length - 1; i >= 0; i -= 1) {
      if (errors[i].path === 'item_name') errors.splice(i, 1);
    }
  } else if (!hasItemId && !itemName) {
    errors.push({ path: 'item_id', message: 'Item is required.' });
    // Also drop the legacy item_name error so the path the form binds
    // to is the brief's `item_id`.
    for (let i = errors.length - 1; i >= 0; i -= 1) {
      if (errors[i].path === 'item_name') errors.splice(i, 1);
    }
  }

  // Per-item-type measurement requirements.
  const itemType = pickStringUpper(line, 'item_type', 'itemType');
  if (itemType === ITEM_TYPE_GLASS) {
    const widthMm  = toNumber(pick(line, 'width_mm',  'widthMm',  0));
    const heightMm = toNumber(pick(line, 'height_mm', 'heightMm', 0));
    if (!(widthMm > 0)) {
      errors.push({
        path: 'width_mm',
        message: 'Width (mm) is required for GLASS lines.'
      });
    }
    if (!(heightMm > 0)) {
      errors.push({
        path: 'height_mm',
        message: 'Height (mm) is required for GLASS lines.'
      });
    }
  } else if (LENGTH_ITEM_TYPES.has(itemType)) {
    const lengthVal = toNumber(pick(line, 'length_value', 'lengthValue', 0));
    if (!(lengthVal > 0)) {
      errors.push({
        path: 'length_value',
        message: 'Length is required for length-driven items.'
      });
    }
  }

  // Rate sanity.
  const rate = toNumber(pick(line, 'rate', 'rate', 0));
  if (rate < 0) {
    errors.push({ path: 'rate', message: 'Rate cannot be negative.' });
  }

  return { ok: errors.length === 0, errors };
}


// ─────────────────────────────────────────────────────────────────
// Header-level payload shape validator — mirrors the backend Joi shape
// in spirit but stays UI-friendly. Returns { ok, errors:[{path,
// message}] } so the form can render inline field errors without
// throwing.
// ─────────────────────────────────────────────────────────────────

export function validatePurchaseReturn(payload) {
  const errors = [];

  if (!payload || typeof payload !== 'object') {
    return { ok: false, errors: [{ path: '', message: 'Payload is required.' }] };
  }

  const returnDate = pick(payload, 'return_date', 'returnDate', null);
  if (returnDate === null || returnDate === undefined || String(returnDate).trim() === '') {
    errors.push({ path: 'return_date', message: 'Return date is required.' });
  }

  // Supplier identity — id OR name (we accept either; backend will
  // resolve the id when only id is present, snapshot the name when
  // only name is present).
  const supplierId   = pick(payload, 'supplier_id', 'supplierId', null);
  const supplierName = pickString(payload, 'supplier_name', 'supplierName');
  const hasSupplierId = supplierId !== null && supplierId !== undefined &&
                        String(supplierId).trim() !== '';
  if (!hasSupplierId && !supplierName) {
    errors.push({ path: 'supplier_id', message: 'Supplier is required.' });
  }

  const returnType = pickStringUpper(payload, 'return_type', 'returnType');
  if (!returnType) {
    errors.push({ path: 'return_type', message: 'Return type is required.' });
  } else if (!RETURN_TYPES_SET.has(returnType)) {
    errors.push({
      path: 'return_type',
      message: `Return type must be one of: ${RETURN_TYPES.join(', ')}.`
    });
  }

  const reasonCode = pickStringUpper(payload, 'reason_code', 'reasonCode');
  if (!reasonCode) {
    errors.push({ path: 'reason_code', message: 'Reason code is required.' });
  } else if (!REASON_CODES_SET.has(reasonCode)) {
    errors.push({
      path: 'reason_code',
      message: `Reason code must be one of: ${REASON_CODES.join(', ')}.`
    });
  }

  const linesRaw = pick(payload, 'lines', 'lines', []);
  const lines = Array.isArray(linesRaw) ? linesRaw : [];
  if (lines.length === 0) {
    errors.push({ path: 'lines', message: 'At least one line is required.' });
  } else {
    lines.forEach((ln, idx) => {
      const r = validatePurchaseReturnLine(ln);
      if (!r.ok) {
        for (const e of r.errors) {
          errors.push({ path: `lines[${idx}].${e.path}`, message: e.message });
        }
      }
    });
  }

  return { ok: errors.length === 0, errors };
}


// ─────────────────────────────────────────────────────────────────
// Header total computation — sums the lines' taxable / cgst / sgst /
// igst into a header summary so the preview footer stays in sync as
// the operator scrolls. Backend recomputes server-side; UI never wins.
//
// Tolerant: ignores non-array input, ignores non-object rows, never
// throws. Totals are rounded half-up to 2dp (rupee precision).
// ─────────────────────────────────────────────────────────────────

export function calculateReturnHeaderTotals(lines, roundOff) {
  let taxable = 0;
  let cgst    = 0;
  let sgst    = 0;
  let igst    = 0;

  if (Array.isArray(lines)) {
    for (const l of lines) {
      if (!l || typeof l !== 'object') continue;
      taxable += toNumber(pick(l, 'taxable_amount', 'taxableAmount', 0));
      cgst    += toNumber(pick(l, 'cgst_amount',    'cgstAmount',    0));
      sgst    += toNumber(pick(l, 'sgst_amount',    'sgstAmount',    0));
      igst    += toNumber(pick(l, 'igst_amount',    'igstAmount',    0));
    }
  }

  const totalTax = round2(cgst + sgst + igst);
  const roundOffRounded = round2(toNumber(roundOff));
  const grandTotal = round2(taxable + totalTax + roundOffRounded);

  return {
    taxableAmount: round2(taxable),
    cgstAmount:    round2(cgst),
    sgstAmount:    round2(sgst),
    igstAmount:    round2(igst),
    totalTax,
    roundOff:      roundOffRounded,
    grandTotal
  };
}


// ─────────────────────────────────────────────────────────────────
// Cross-line guards — true/false predicates the form can use to
// disable the Save / Confirm / Post CTA. Mirror the backend cross-
// field guards.
// ─────────────────────────────────────────────────────────────────

export function validateAllSameSupplier(lines, supplierId) {
  if (!Array.isArray(lines) || lines.length === 0) return true;
  if (!supplierId) return true;

  const sid = String(supplierId);
  for (const l of lines) {
    if (!l || typeof l !== 'object') continue;
    const lineSupplierId = pick(l, 'supplier_id', 'supplierId', null);
    if (lineSupplierId && String(lineSupplierId) !== sid) {
      return false;
    }
  }
  return true;
}

export function validateHeaderMatchesLines(payload) {
  if (!payload || typeof payload !== 'object') return false;

  const lines = pick(payload, 'lines', 'lines', []);
  const totals = calculateReturnHeaderTotals(
    Array.isArray(lines) ? lines : [],
    pick(payload, 'round_off', 'roundOff', 0)
  );

  const headerGrand = round2(toNumber(pick(payload, 'grand_total', 'grandTotal', totals.grandTotal)));
  return Math.abs(headerGrand - totals.grandTotal) <= BALANCE_EPSILON;
}


// ─────────────────────────────────────────────────────────────────
// Capability gates — drive Edit / Confirm / Post / Cancel / Delete
// CTAs in the drawer + grid. The backend remains the authority; these
// guards hide or disable CTAs for operators who clearly can't succeed.
//
//   Edit:     DRAFT or POSTING_FAILED
//   Confirm:  DRAFT
//   Post:     CONFIRMED or POSTING_FAILED
//   Cancel:   POSTED or POSTING_FAILED
//   Delete:   DRAFT and no voucher
// ─────────────────────────────────────────────────────────────────

function normalizeStatus(status) {
  if (!status) return '';
  return String(status).trim().toUpperCase();
}

export function canEditPurchaseReturn(status) {
  const key = normalizeStatus(status);
  return key === STATUS_DRAFT || key === STATUS_POSTING_FAILED;
}

export function canConfirmPurchaseReturn(status) {
  const key = normalizeStatus(status);
  return key === STATUS_DRAFT;
}

export function canPostPurchaseReturn(status) {
  const key = normalizeStatus(status);
  return key === STATUS_CONFIRMED || key === STATUS_POSTING_FAILED;
}

export function canCancelPurchaseReturn(status) {
  const key = normalizeStatus(status);
  return (
    key === STATUS_DRAFT ||
    key === STATUS_CONFIRMED ||
    key === STATUS_POSTING_FAILED ||
    key === STATUS_POSTED
  );
}

export function canDeletePurchaseReturn(status, hasVoucher) {
  const key = normalizeStatus(status);
  if (key !== STATUS_DRAFT) return false;
  return !hasVoucher;
}


// ─────────────────────────────────────────────────────────────────
// Status labels + tag colours. Colours align with the AntD Tag
// palette guidance in docs/23-antd-component-standards.md so the
// return grids match the rest of the control-plane chrome.
// ─────────────────────────────────────────────────────────────────

const RETURN_STATUS_LABELS = {
  DRAFT:          'Draft',
  CONFIRMED:      'Confirmed',
  POSTED:         'Posted',
  POSTING_FAILED: 'Posting Failed',
  CANCELLED:      'Cancelled'
};

const RETURN_STATUS_COLORS = {
  DRAFT:          'default',
  CONFIRMED:      'processing',
  POSTED:         'success',
  POSTING_FAILED: 'error',
  CANCELLED:      'default'
};

export function returnStatusLabel(status) {
  if (!status) return '';
  const key = normalizeStatus(status);
  return RETURN_STATUS_LABELS[key] || key;
}

export function returnStatusColor(status) {
  if (!status) return 'default';
  const key = normalizeStatus(status);
  return RETURN_STATUS_COLORS[key] || 'default';
}

const POSTING_STATUS_LABELS = {
  NOT_POSTED:     'Not Posted',
  POSTED:         'Posted',
  POSTING_FAILED: 'Posting Failed',
  CANCELLED:      'Cancelled'
};

const POSTING_STATUS_COLORS = {
  NOT_POSTED:     'default',
  POSTED:         'success',
  POSTING_FAILED: 'error',
  CANCELLED:      'default'
};

export function postingStatusLabel(status) {
  if (!status) return '';
  const key = normalizeStatus(status);
  return POSTING_STATUS_LABELS[key] || key;
}

export function postingStatusColor(status) {
  if (!status) return 'default';
  const key = normalizeStatus(status);
  return POSTING_STATUS_COLORS[key] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Return-type + reason-code labels — human-readable text for the
// taxonomies.
// ─────────────────────────────────────────────────────────────────

const RETURN_TYPE_LABELS = {
  MATERIAL_RETURN:     'Material Return',
  RATE_ADJUSTMENT:     'Rate Adjustment',
  TAX_ADJUSTMENT:      'Tax Adjustment',
  QUANTITY_ADJUSTMENT: 'Quantity Adjustment'
};

export function returnTypeLabel(type) {
  if (!type) return '';
  const key = normalizeStatus(type);
  return RETURN_TYPE_LABELS[key] || key.replace(/_/g, ' ');
}

const REASON_CODE_LABELS = {
  DAMAGED:                  'Damaged',
  WRONG_ITEM:               'Wrong Item',
  QUALITY_REJECTED:         'Quality Rejected',
  SHORT_SUPPLY_ADJUSTMENT:  'Short Supply',
  RATE_DIFFERENCE:          'Rate Difference',
  EXCESS_BILLING:           'Excess Billing',
  SUPPLIER_REPLACEMENT:     'Supplier Replacement',
  OTHER:                    'Other'
};

export function reasonCodeLabel(code) {
  if (!code) return '';
  const key = normalizeStatus(code);
  return REASON_CODE_LABELS[key] || key.replace(/_/g, ' ');
}

const REVERSAL_STATUS_LABELS = {
  REVERSED:  'Reversed',
  CANCELLED: 'Cancelled'
};

const REVERSAL_STATUS_COLORS = {
  REVERSED:  'success',
  CANCELLED: 'default'
};

export function reversalStatusLabel(status) {
  if (!status) return '';
  const key = normalizeStatus(status);
  return REVERSAL_STATUS_LABELS[key] || key;
}

export function reversalStatusColor(status) {
  if (!status) return 'default';
  const key = normalizeStatus(status);
  return REVERSAL_STATUS_COLORS[key] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// summarizePurchaseReturn — single-glance summary for the return
// detail card. Returns the lines array, totals, the debit-note
// number (when posted), and the return status.
//
// Tolerant: ignores non-object input, never throws.
// ─────────────────────────────────────────────────────────────────

export function summarizePurchaseReturn(returnObj) {
  const out = {
    lines:         [],
    taxableAmount: 0,
    cgstAmount:    0,
    sgstAmount:    0,
    igstAmount:    0,
    totalTax:      0,
    roundOff:      0,
    grandTotal:    0,
    debitNoteNo:   null,
    voucherNo:     null,
    status:        null,
    postingStatus: null,
    returnStatus:  null
  };

  if (!returnObj || typeof returnObj !== 'object') return out;

  const linesRaw = pick(returnObj, 'lines', 'lines', []);
  const lines = Array.isArray(linesRaw) ? linesRaw : [];
  out.lines = lines;

  // Prefer header totals when present (post-save snapshot); fall
  // back to recompute when only the editor draft is in hand.
  const hdrTaxable = pick(returnObj, 'taxable_amount', 'taxableAmount', undefined);
  const hdrGrand   = pick(returnObj, 'grand_total',    'grandTotal',    undefined);

  if (hdrTaxable !== undefined && hdrTaxable !== null && hdrTaxable !== '') {
    out.taxableAmount = round2(toNumber(hdrTaxable));
    out.cgstAmount    = round2(toNumber(pick(returnObj, 'cgst_amount', 'cgstAmount', 0)));
    out.sgstAmount    = round2(toNumber(pick(returnObj, 'sgst_amount', 'sgstAmount', 0)));
    out.igstAmount    = round2(toNumber(pick(returnObj, 'igst_amount', 'igstAmount', 0)));
    const totalTaxRaw = pick(returnObj, 'total_tax', 'totalTax', undefined);
    out.totalTax      = totalTaxRaw !== undefined && totalTaxRaw !== null && totalTaxRaw !== ''
      ? round2(toNumber(totalTaxRaw))
      : round2(out.cgstAmount + out.sgstAmount + out.igstAmount);
    out.roundOff      = round2(toNumber(pick(returnObj, 'round_off', 'roundOff', 0)));
    out.grandTotal    = hdrGrand !== undefined && hdrGrand !== null && hdrGrand !== ''
      ? round2(toNumber(hdrGrand))
      : round2(out.taxableAmount + out.totalTax + out.roundOff);
  } else {
    const totals = calculateReturnHeaderTotals(
      lines,
      pick(returnObj, 'round_off', 'roundOff', 0)
    );
    out.taxableAmount = totals.taxableAmount;
    out.cgstAmount    = totals.cgstAmount;
    out.sgstAmount    = totals.sgstAmount;
    out.igstAmount    = totals.igstAmount;
    out.totalTax      = totals.totalTax;
    out.roundOff      = totals.roundOff;
    out.grandTotal    = totals.grandTotal;
  }

  out.debitNoteNo   = pick(returnObj, 'debit_note_no',         'debitNoteNo',         null) || null;
  out.voucherNo     = pick(returnObj, 'accounting_voucher_no', 'accountingVoucherNo', null) || null;
  out.status        = pickStringUpper(returnObj, 'status',         'status')        || null;
  out.postingStatus = pickStringUpper(returnObj, 'posting_status', 'postingStatus') || null;
  out.returnStatus  = out.status;

  return out;
}


// ─────────────────────────────────────────────────────────────────
// calculateLineTax — shape adapter that bridges the form's flat
// `{ ...line, interState }` call shape onto the canonical
// `calculatePurchaseReturnLine(line, { isInterState })` helper.
//
// PurchaseReturnFormPage spreads the in-progress line + the header
// interState flag into a single object for the live recompute path,
// then reads `tax.grossAmount`, `tax.discountAmount`, `tax.taxableAmount`,
// `tax.cgstAmount`, `tax.sgstAmount`, `tax.igstAmount`, `tax.lineTotal`.
// calculatePurchaseReturnLine returns both casings for every field so
// no further translation is needed — this adapter only normalises the
// INPUT shape.
//
// Accepts either `interState` or `isInterState`; defaults to false if
// neither is present. Input is treated read-only.
// ─────────────────────────────────────────────────────────────────
export function calculateLineTax(input) {
  if (!input || typeof input !== 'object') {
    return calculatePurchaseReturnLine({}, { isInterState: false });
  }
  const { interState, isInterState, ...line } = input;
  const flag = (isInterState !== undefined) ? isInterState : interState;
  return calculatePurchaseReturnLine(line, { isInterState: !!flag });
}


// ─────────────────────────────────────────────────────────────────
// Sanity references — keep the importer pattern at the top of this
// module honest. Touching these here keeps a future refactor from
// silently dropping an export.
// ─────────────────────────────────────────────────────────────────
void RETURN_TYPES_SET;
void REASON_CODES_SET;
void RETURN_STATUS_SET;
void STATUS_CONFIRMED;
